export const BACKEND_ERROR_CODES = [
  'VALIDATION_ERROR',
  'UNAUTHENTICATED',
  'CONFLICT',
  'BUSINESS_RULE',
  'CONNECTOR_UNSUPPORTED',
  'RATE_LIMITED',
  'INTERNAL_ERROR',
] as const;

export type BackendErrorCode = (typeof BACKEND_ERROR_CODES)[number];

export const SYNTHETIC_ERROR_CODES = [
  'NETWORK_ERROR',
  'TIMEOUT',
  'OFFLINE',
  'PARSE_ERROR',
  'ABORTED',
  'UNKNOWN',
] as const;

export type SyntheticErrorCode = (typeof SYNTHETIC_ERROR_CODES)[number];

/**
 * Maps HTTP status + backend `error.code` onto our i18n keys. Always returns a real key; the final
 * fallback is `errors:generic.unexpected`, which exists in en/es/pt.
 *
 * Never returns a backend `error.message`. Those are operator-facing English (OD-C4).
 */
export function mapErrorToMessageKey(status: number | undefined, code: string): string {
  if (code === 'NETWORK_ERROR') return 'errors:network.body';
  if (code === 'TIMEOUT') return 'errors:timeout.body';
  if (code === 'OFFLINE') return 'errors:offline.body';
  if (code === 'PARSE_ERROR') return 'errors:parse.body';

  if (status === 401 || code === 'UNAUTHENTICATED') return 'errors:http.401.body';
  if (status === 409 || code === 'CONFLICT') return 'errors:http.409.body';
  if (status === 429 || code === 'RATE_LIMITED') return 'errors:http.429.body';
  // Amount-outside-limits, no wallet route, and similar — not specifically "this method".
  if (code === 'BUSINESS_RULE') return 'errors:business.rule.body';
  if (status === 422 || code === 'CONNECTOR_UNSUPPORTED') {
    return 'errors:business.methodUnavailable.body';
  }
  if (status === 400 || code === 'VALIDATION_ERROR') return 'errors:http.400.body';
  if (status === 403) return 'errors:http.403.body';
  if (status === 404) return 'errors:http.404.body';
  if (status === 413) return 'errors:http.413.body';
  if (status === 502) return 'errors:http.502.body';
  if (status === 503) return 'errors:http.503.body';
  if (status === 504) return 'errors:http.504.body';
  if (status !== undefined && status >= 500) return 'errors:http.500.body';

  return 'errors:generic.unexpected';
}

const DECIMAL_AMOUNT = /^-?\d+(\.\d+)?$/;
const AMOUNT_BELOW_MIN = /below the minimum pay-in limit \(([^)]+)\)/i;
const AMOUNT_ABOVE_MAX = /above the maximum pay-in limit \(([^)]+)\)/i;

const NO_WALLET_ROUTE = /wallet[- ]based route|wallet route/i;

/** Stable English shape for a UTR already claimed on another completed / pending-review pay-in. */
const DUPLICATE_UTR = /UTR is already used/i;

/**
 * `BUSINESS_RULE` covers amount limits and method/route failures. The backend does not expose a
 * messageKey (OD-C4), so we classify the stable English shapes we know and keep the rest on the
 * generic rule copy. The captured amount is a decimal string; the backend sentence is never shown.
 */
export function classifyBusinessRule(message: string): {
  messageKey: string;
  limitAmount?: string | undefined;
} {
  const below = AMOUNT_BELOW_MIN.exec(message)?.[1]?.trim();
  if (below !== undefined && DECIMAL_AMOUNT.test(below)) {
    return { messageKey: 'errors:business.amountBelowMin.body', limitAmount: below };
  }
  const above = AMOUNT_ABOVE_MAX.exec(message)?.[1]?.trim();
  if (above !== undefined && DECIMAL_AMOUNT.test(above)) {
    return { messageKey: 'errors:business.amountAboveMax.body', limitAmount: above };
  }
  if (NO_WALLET_ROUTE.test(message)) {
    return { messageKey: 'errors:business.methodUnavailable.body' };
  }
  return { messageKey: 'errors:business.rule.body' };
}

/**
 * Most `CONFLICT` responses mean the UI is out of sync (refetch + banner). Duplicate-UTR is the
 * exception: the session is still valid and the player must correct the field. Same OD-C4 shape
 * matching as `classifyBusinessRule` — the English sentence is never shown.
 */
export function classifyConflict(message: string):
  | { messageKey: string; fieldErrors: Record<string, string> }
  | undefined {
  if (DUPLICATE_UTR.test(message)) {
    return {
      messageKey: 'proof:errors.duplicateUtr',
      fieldErrors: { utr: 'proof:errors.duplicateUtr' },
    };
  }
  return undefined;
}

/** Body keys from `mapErrorToMessageKey` pair with a `.title` sibling; the unexpected fallback does not. */
export function titleKeyFromMessageKey(messageKey: string): string {
  if (messageKey.endsWith('.body')) {
    return `${messageKey.slice(0, -'.body'.length)}.title`;
  }
  return 'errors:generic.title';
}

const BACKEND_FIELD_TO_MESSAGE_KEY: Readonly<Record<string, string>> = {
  utr: 'proof:errors.utrRequired',
  file: 'proof:errors.fileRequired',
  payerWalletNumber: 'methods:payerWallet.errors.invalid',
};

/** Backend `fields[].message` is discarded. We map the field name onto our own key. */
export function mapFieldErrorKey(field: string): string {
  return BACKEND_FIELD_TO_MESSAGE_KEY[field] ?? 'errors:http.400.body';
}
